import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StandardInputField from '../StandardComponents/StandardInputField/StandardInputField';
import { getRetentionSettings, updateRetentionSettings, hardDeleteArchived } from '../../Api/SystemEmailLogs';

export default function EmailNotificationsSettings({ open, onClose, organizationId }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [settings, setSettings] = useState(null);
  const [retentionDays, setRetentionDays] = useState('');
  const [deleteBeforeDate, setDeleteBeforeDate] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (open) {
      if (!organizationId) {
        setError('Please select a specific organization to manage retention settings');
        setLoading(false);
        return;
      }
      fetchSettings();
    }
  }, [open, organizationId]);

  const fetchSettings = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await getRetentionSettings(organizationId);
      setSettings(data);
      setRetentionDays(data.retention_days?.toString() || '');
    } catch (err) {
      setError('Failed to load retention settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const days = retentionDays ? parseInt(retentionDays) : null;
      
      if (days !== null && (days < 90 || days > 270)) {
        setError('Retention days must be between 90 and 270');
        setLoading(false);
        return;
      }

      await updateRetentionSettings({
        organization_id: organizationId,
        retention_days: days,
      });
      
      setSuccess('Retention settings updated successfully');
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      const errorMsg = err?.response?.data?.error || err?.message || 'Failed to update retention settings';
      setError(errorMsg);
      console.error('Failed to update retention settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleHardDelete = async () => {
    if (!deleteBeforeDate) {
      setError('Please select a date');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const { data } = await hardDeleteArchived(deleteBeforeDate);
      setSuccess(`Successfully deleted ${data.count} archived log(s)`);
      setShowDeleteConfirm(false);
      setDeleteBeforeDate('');
    } catch (err) {
      setError('Failed to delete archived logs');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          backgroundColor: '#FFFFFF',
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '24px 32px',
          borderBottom: '1px solid #E4E6EF',
          backgroundColor: '#FFFFFF',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontSize: '20px',
            fontWeight: 600,
            color: '#1E2737',
          }}
        >
          Email Notifications Retention Settings
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: '#6B7280',
            '&:hover': {
              backgroundColor: '#F3F4F6'
            }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          padding: '32px',
          backgroundColor: '#FFFFFF',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#F3F4F6',
            borderRadius: '3px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#9CA3AF',
            borderRadius: '3px',
            '&:hover': {
              background: '#6B7280'
            }
          },
        }}
      >
        {loading && !settings ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            <Box mb={3}>
              <Typography 
                variant="body2" 
                sx={{
                  fontSize: '14px',
                  color: '#6B7280',
                }}
                gutterBottom
              >
                Configure how long email notification logs are retained before being archived.
              </Typography>
            </Box>

            <Box mb={3}>
              <Typography 
                variant="subtitle2"
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#1E2737',
                  marginBottom: '8px',
                }}
                gutterBottom
              >
                Global Default
              </Typography>
              <Typography 
                variant="body2"
                sx={{
                  fontSize: '14px',
                  color: '#6B7280',
                }}
              >
                {settings?.global_default_days || 180} days
              </Typography>
            </Box>

            <Box mb={3}>
              <StandardInputField
                type="number"
                name="retention_days"
                label="Organization Retention Period (days)"
                value={retentionDays}
                onChange={(e) => setRetentionDays(e.target.value)}
                config={{
                  uiStyle: 'field_styling_25',
                  placeholder: 'Leave empty to use global default',
                  helperText: `Must be between ${settings?.min_allowed_days || 90} and ${settings?.max_allowed_days || 270} days.`,
                  inputProps: {
                    min: settings?.min_allowed_days || 90,
                    max: settings?.max_allowed_days || 270,
                  }
                }}
              />
            </Box>

            <Divider 
              sx={{ 
                my: 3,
                borderColor: '#E4E6EF',
              }} 
            />

            <Box>
              <Typography 
                variant="subtitle2"
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#DC2626',
                  marginBottom: '8px',
                }}
                gutterBottom
              >
                Danger Zone
              </Typography>
              <Typography 
                variant="body2"
                sx={{
                  fontSize: '14px',
                  color: '#6B7280',
                  marginBottom: '16px',
                }}
                paragraph
              >
                Permanently delete archived logs. This action cannot be undone.
              </Typography>

              {!showDeleteConfirm ? (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  Hard Delete Archived Logs
                </Button>
              ) : (
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#374151',
                      fontWeight: 500,
                      mb: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}
                  >
                    Delete logs archived before
                  </Typography>
                  <TextField
                    type="date"
                    fullWidth
                    value={deleteBeforeDate}
                    onChange={(e) => setDeleteBeforeDate(e.target.value)}
                    sx={{
                      mb: 1,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#FFFFFF',
                        borderRadius: '8px',
                        '& fieldset': { borderColor: '#E5E7EB' },
                        '&:hover fieldset': { 
                          borderColor: '#D1D5DB' 
                        },
                        '&.Mui-focused fieldset': { 
                          borderColor: '#3B82F6' 
                        }
                      },
                      '& .MuiInputBase-input': {
                        color: '#111827',
                      }
                    }}
                  />
                  <Box display="flex" gap={1}>
                    <Button
                      variant="contained"
                      color="error"
                      size="small"
                      onClick={handleHardDelete}
                      disabled={loading || !deleteBeforeDate}
                    >
                      Confirm Delete
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setShowDeleteConfirm(false);
                        setDeleteBeforeDate('');
                        setError('');
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          padding: '16px 32px',
          borderTop: '1px solid #E4E6EF',
          backgroundColor: '#FFFFFF',
          gap: '12px'
        }}
      >
        <Button 
          onClick={onClose} 
          disabled={loading}
          variant="outlined"
          sx={{
            color: '#1E2737',
            borderColor: '#D1D5DB',
            '&:hover': {
              borderColor: '#9CA3AF',
              backgroundColor: '#F9FAFB',
            }
          }}
        >
          Close
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          sx={{
            backgroundColor: '#1E2737',
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#374151',
            },
            '&:disabled': {
              backgroundColor: '#D1D5DB',
              color: '#9CA3AF',
            }
          }}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

